// =============================================================================
// useInstructorScheduleIntervals — CRUD for instructor_schedule_intervals
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ScheduleInterval {
  id:          string;
  day_of_week: string; // 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun'
  start_time:  string; // "HH:MM:SS"
  end_time:    string; // "HH:MM:SS"
}

export interface NewInterval {
  day_of_week: string;
  start_time:  string; // "HH:MM"
  end_time:    string; // "HH:MM"
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export const useInstructorScheduleIntervals = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // ── Read ────────────────────────────────────────────────────────────────────
  const {
    data: intervals = [],
    isLoading,
    error,
  } = useQuery<ScheduleInterval[]>({
    queryKey: ['instructor-schedule-intervals', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase.rpc('get_instructor_schedule_intervals', {
        p_instructor_id: user.id,
      });

      if (error) throw error;
      return (data as ScheduleInterval[]) ?? [];
    },
    enabled: !!user,
    staleTime: 60 * 1000, // 1 min — intervals change less often than slots
  });

  // ── Add ─────────────────────────────────────────────────────────────────────
  const addIntervalMutation = useMutation({
    mutationFn: async (newInterval: NewInterval) => {
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('instructor_schedule_intervals')
        .insert({
          instructor_id: user.id,
          day_of_week:   newInterval.day_of_week,
          start_time:    newInterval.start_time,
          end_time:      newInterval.end_time,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instructor-schedule-intervals', user?.id] });
      // Also invalidate slot queries so the calendar and public page refresh
      queryClient.invalidateQueries({ queryKey: ['instructor-slots', user?.id] });
    },
  });

  // ── Delete ──────────────────────────────────────────────────────────────────
  const deleteIntervalMutation = useMutation({
    mutationFn: async (intervalId: string) => {
      const { error } = await supabase
        .from('instructor_schedule_intervals')
        .delete()
        .eq('id', intervalId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instructor-schedule-intervals', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['instructor-slots', user?.id] });
    },
  });

  return {
    intervals,
    isLoading,
    error: error?.message ?? null,
    addInterval:     addIntervalMutation.mutateAsync,
    deleteInterval:  deleteIntervalMutation.mutateAsync,
    isAdding:        addIntervalMutation.isPending,
    isDeleting:      deleteIntervalMutation.isPending,
    addError:        addIntervalMutation.error?.message ?? null,
  };
};
